import React, { useCallback, forwardRef } from 'react'
import { useDispatch } from 'react-redux'

import PropTypes from 'prop-types'

import { useDialog } from '@galo-monitor/dialog'
import loginSchema from '@galo-monitor/forms/schemas/auth/login.schema'
import I18n, { useT } from '@galo-monitor/i18n'
import { AuthenticationActions } from '@galo-monitor/stores/authentication'
import Button from '@galo-monitor/web-components/Button'
import Checkbox from '@galo-monitor/web-components/Checkbox'
import Form from '@galo-monitor/web-components/Form'
import InputEmail from '@galo-monitor/web-components/InputEmail'
import InputPassword from '@galo-monitor/web-components/InputPassword'
import ErrorModal from '@galo-monitor/web-containers/modals/ErrorModal'
import LoadingModal from '@galo-monitor/web-containers/modals/LoadingModal'

import useStyles, { Container, Title } from './styles'

const LoginForm = forwardRef((props, formRef) => {
  const { withoutSubmitButton, loading, onSubmit } = props

  const classes = useStyles()
  const dispatch = useCallback(useDispatch(), [])
  const { createDialog, removeDialog } = useDialog()
  const t = useT()

  const handleSubmit = useCallback(
    ({ email, password }) => {
      createDialog({
        id: 'loading',
        Component: LoadingModal
      })

      const removeLoader = () => {
        setTimeout(() => removeDialog({ id: 'loading' }), 150)
      }

      dispatch(AuthenticationActions.login(
        email,
        password,
        () => {
          onSubmit()
          removeLoader()
        },
        err => {
          const defaultErrorTitle = 'unexpected error occurred'
          const defaultErrorMessage = 'try again contact support'

          let errorProps = {
            title: t('access not authorized'),
            message: t('login error message')
          }

          switch(err.response.status) {
            case 400:
              errorProps = {
                title: t(err?.response?.data?.errors?.[0]?.message ?? 'access not authorized', { type: 'title' }),
                message: t(err?.response?.data?.errors?.[0]?.message ?? 'login error message', { type: 'message' })
              }
              break
            case 403:
              errorProps = {
                title: t('access not authorized'),
                message: t(err?.response?.data?.message ?? defaultErrorTitle, { screen: 'login' })
              }
              break
            case 404:
              errorProps = {
                title: t('reset password error title'),
                message: t('reset password error message')
              }
              break
            default:
              errorProps = {
                title: t(defaultErrorTitle),
                message: t(defaultErrorMessage)
              }
          }

          createDialog({
            id: 'login-error',
            Component: ErrorModal,
            props: errorProps
          })
          removeLoader()
        }
      ))
    },
    [createDialog, dispatch, onSubmit, removeDialog, t]
  )

  const submitOnEnter = useCallback(({ key }) => key === 'Enter' && formRef.current.submit(), [formRef])

  return (
    <Form
      ref={ formRef }
      schemaConstructor={ loginSchema }
      onSubmit={ handleSubmit }
    >
      <Container>
        <Title>
          <I18n>access</I18n>
        </Title>

        <InputEmail
          name="email"
          label="E-mail"
          disabled={ loading }
          onKeyUp={ submitOnEnter }
          fullWidth
        />

        <InputPassword
          name="password"
          label={ t('password') }
          disabled={ loading }
          className={ classes.password }
          onKeyUp={ submitOnEnter }
          fullWidth
        />

        <Checkbox
          name="remember"
          label={ t('remember me') }
          disabled={ loading }
        />

        {!withoutSubmitButton && (
          <Button
            id="signup-submit"
            onClick={ () => formRef.current.submit() }
            disabled={ loading }
            fullWidth
          >
            <I18n>login</I18n>
          </Button>
        )}
      </Container>
    </Form>
  )
})

LoginForm.propTypes = {
  loading: PropTypes.bool,
  onSubmit: PropTypes.func,
  withoutSubmitButton: PropTypes.bool
}

LoginForm.defaultProps = {
  loading: false,
  onSubmit: () => {},
  withoutSubmitButton: false
}

export default LoginForm
